import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { Brand } from "../../../shared/types";
import { BrandProfile } from "../components/brand/BrandProfile";
import { PrimaryCallToAction } from "../components/brand/PrimaryCallToAction";
import { useActiveBrand } from "../hooks/useActiveBrand";
import {
  useBrand,
  useUpdateBrand,
} from "../hooks/useBrand";
import { BrandCorrections } from "./BrandCorrections";
import { BrandVisuals } from "./BrandVisuals";
import { BrandSurfaces } from "./BrandSurfaces";
import { ArtifactRouteRecovery } from "../components/brand/ArtifactRouteRecovery";
import { buildAccountConnectionsPath } from "../lib/brand-navigation";
import { OperatorSurface } from "../components/ui/OperatorSurface";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";

type BrandOutletContext = {
  brand: Brand;
  brandId: string;
};

const BRAND_SECTION_LABELS: Record<string, string> = {
  profile: "Profile",
  voice: "Voice",
  "context-docs": "Context docs",
  surfaces: "Surfaces",
  corrections: "Corrections",
};

export function useBrandPageContext(): BrandOutletContext {
  return useOutletContext<BrandOutletContext>();
}

export function useConsumedCreatedBrandName(): string | undefined {
  const location = useLocation();
  const navigate = useNavigate();
  const [createdBrandName] = useState(
    () => (location.state as { createdBrandName?: string } | null)?.createdBrandName,
  );

  useEffect(() => {
    if (!(location.state as { createdBrandName?: string } | null)?.createdBrandName) return;
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null },
    );
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  return createdBrandName;
}

export function BrandLayout() {
  const { brandId } = useParams<{ brandId: string }>();
  const location = useLocation();
  const { activeBrand, isLoading } = useActiveBrand();
  const { data: brandDetail, isLoading: isBrandLoading, isError } = useBrand(brandId);

  if (!brandId) {
    return (
      <ArtifactRouteRecovery
        eyebrow="Brand settings"
        title="This brand utility route is incomplete"
        message="The link is missing the brand context needed to open this secondary utility route."
        primaryAction={{ href: "/brand", label: "Choose a brand" }}
        secondaryAction={{ href: "/home", label: "Back to workspace" }}
      />
    );
  }

  const brand = brandDetail?.data ?? (activeBrand?.id === brandId ? activeBrand : undefined);

  if (isLoading || (!brand && isBrandLoading)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (isError && !brand) {
    return (
      <ArtifactRouteRecovery
        eyebrow="Brand utilities"
        title="This brand utility route could not be opened"
        message="The brand may have been removed, the link may be stale, or you may no longer have access. Return to a safe surface instead of staying on an empty shell."
        primaryAction={{ href: "/brand", label: "Choose a brand" }}
        secondaryAction={{ href: "/home", label: "Back to workspace" }}
      />
    );
  }

  if (!brand) {
    return (
      <ArtifactRouteRecovery
        eyebrow="Brand utilities"
        title="This brand utility route is no longer available"
        message="The saved brand utility route no longer resolves to an accessible brand. Pick another brand or return to the workspace."
        primaryAction={{ href: "/brand", label: "Choose a brand" }}
        secondaryAction={{ href: "/home", label: "Back to workspace" }}
      />
    );
  }

  const isUtilityRoute = /^\/brand\/[^/]+\/settings(\/|$)/.test(location.pathname);

  if (!isUtilityRoute) {
    return <Outlet context={{ brand, brandId }} />;
  }

  const sectionKey = location.pathname.split("/").filter(Boolean).at(-1) ?? "";
  const currentSection = BRAND_SECTION_LABELS[sectionKey] ?? "Brand settings";

  return (
    <div className="mx-auto max-w-6xl space-y-5 px-4 py-4 md:px-6">
      <WorkspaceHeader
        eyebrow="Brand"
        title={brand.name}
        description="Manage this brand’s identity, settings, and work."
        breadcrumbs={[
          { label: "Brands", href: "/brands" },
          { label: brand.name },
          { label: currentSection },
        ]}
      />

      <Outlet context={{ brand, brandId }} />
    </div>
  );
}

export function BrandProfilePage() {
  const { brand, brandId } = useBrandPageContext();
  const updateBrand = useUpdateBrand(brandId);
  const createdBrandName = useConsumedCreatedBrandName();

  return (
    <div className="space-y-4">
      {createdBrandName ? (
        <OperatorSurface density="compact" className="border-green-200 bg-green-50 text-sm text-green-800" as="p">
          <span role="status">✓ {createdBrandName} was created.</span>
        </OperatorSurface>
      ) : null}
      <BrandProfile key={brand.id} brand={brand} onSave={(data) => updateBrand.mutate(data)} saving={updateBrand.isPending} />
      <OperatorSurface density="compact"><PrimaryCallToAction key={brand.id} brandId={brand.id} /></OperatorSurface>
    </div>
  );
}

export function BrandConnectionsPage() {
  const { brandId } = useBrandPageContext();
  return <Navigate to={buildAccountConnectionsPath(brandId)} replace />;
}

export function BrandCorrectionsPage() {
  const { brandId } = useBrandPageContext();
  return <BrandCorrections brandId={brandId} />;
}

export function BrandVisualsPage() {
  const { brandId } = useBrandPageContext();
  return <BrandVisuals brandId={brandId} />;
}

export function BrandSurfacesPage() {
  return <BrandSurfaces />;
}
